import React from 'react';
import { AutocompleteRenderInputParams, FormControl, IconButton, InputAdornment } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';
import { styled } from 'styled-components';

import { ProtectorType } from 'core/constants';
import { colors } from 'theme/colors';
import { EnumOptions } from 'utils/api/modules/entities/entities.types';

import { StyledAutocomplete, StyledTextField as BaseTextField } from 'components/Input/styles';

import Chip from 'components/Chip';

type EntitiesDropDownFieldProps = {
  type: ProtectorType;
  value: string[];
  items: EnumOptions;
  description: string;
  onChange: (value: string[]) => void;
  requiredNotFilled?: boolean;
  error?: boolean;
  helperText?: string;
};

const StyledTextField = styled(BaseTextField)`
  & .Mui-error.MuiFormHelperText-root {
    background-color: ${colors.grey[100]} !important;
  }
`;

export const EntitiesDropDownField = ({
  type,
  value,
  items,
  onChange,
  description,
  requiredNotFilled,
  error,
  helperText,
}: EntitiesDropDownFieldProps): React.ReactElement => {
  const [currentValue, setCurrentValue] = React.useState(value);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [firstClick, setFirstClick] = React.useState(true);
  const [open, setOpen] = React.useState(false);

  const toggleInputMode = (): void => {
    if (inputRef.current) {
      setOpen(true);
      inputRef.current.setAttribute('inputmode', firstClick ? 'none' : 'text');
      inputRef.current.setAttribute('aria-expanded', 'true');
      setFirstClick(false);
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onChange([]);
    setCurrentValue([]);
  };

  const getAutocompleteValue = (val: string[]): { key: string; value: string }[] | undefined => {
    if (!val || val.length === 0) {
      return [];
    }
    if (type === ProtectorType.MultiChoice) {
      return val
        .map((key) => items.find((item) => item.key === key))
        .filter((item): item is { key: string; value: string } => item !== undefined);
    }

    const singleItem = items.find((item) => item.key === val[0]);

    // @ts-ignore
    return singleItem || [];
  };

  const placeholder = type === ProtectorType.MultiChoice ? 'Choose option(s)' : 'Choose an option';

  const renderTags = (
    tagValue: { key: string; value: string }[],
    getTagProps: (params: { index: number }) => { key: number }
  ): React.ReactElement[] =>
    tagValue.map((option, index) => (
      <Chip {...getTagProps({ index })} label={option?.value || ''} fontSize={14} variant="filled" />
    ));

  const renderInput = (params: AutocompleteRenderInputParams): React.ReactElement => (
    <StyledTextField
      {...params}
      error={requiredNotFilled || error}
      helperText={helperText}
      inputRef={inputRef}
      placeholder={value.length > 0 ? '' : placeholder}
      onClick={toggleInputMode}
      InputProps={{
        ...params.InputProps,
        sx: {
          paddingRight: '9px !important',
        },
        endAdornment: (
          <InputAdornment position="end" sx={{ height: 'auto' }}>
            {currentValue && (
              <IconButton onClick={handleClear} aria-label="Clear" title="Clear" sx={{ padding: '4px' }}>
                <ClearIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            )}

            <IconButton onClick={toggleInputMode} aria-label="toggle dropdown" sx={{ padding: '4px' }}>
              {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const handleOnChange = (
    _: React.SyntheticEvent | null,
    newValue: { key: string; value: string } | { key: string; value: string }[] | null
  ): void => {
    if (newValue) {
      const keys = Array.isArray(newValue) ? newValue.map((item) => item.key) : [newValue.key];
      onChange(keys);
      setCurrentValue(keys);
    } else {
      onChange([]);
      setCurrentValue([]);
    }
  };

  const handleOnInputChange = (_: React.SyntheticEvent | null, __: string, reason: string): void => {
    if (reason === 'clear') {
      onChange([]);
      setCurrentValue([]);
    }
  };

  const getOptionLabel = (option: { key: string; value: string }): string => option.value || '';

  return (
    <FormControl fullWidth>
      <StyledAutocomplete
        fullWidth
        open={open}
        size="medium"
        disablePortal
        role="textbox"
        options={items}
        data-testid={description}
        key={`${description}-${type}`}
        onClose={() => setOpen(false)}
        value={getAutocompleteValue(currentValue)}
        multiple={type === ProtectorType.MultiChoice}
        disableCloseOnSelect={type === ProtectorType.MultiChoice}
        isOptionEqualToValue={(option, val) => option?.key === val?.key}
        id={`${type === ProtectorType.MultiChoice ? 'multi' : 'single'}-drop-down-field`}
        limitTags={2}
        renderTags={renderTags}
        renderInput={renderInput}
        style={{
          height: 'fit-content',
          width: '100%',
        }}
        sx={{
          '& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
            fontSize: '16px',
          },
          '& + .MuiChip-root': {
            fontSize: '16px',
          },
        }}
        onChange={handleOnChange}
        onInputChange={handleOnInputChange}
        getOptionLabel={getOptionLabel}
        disableClearable={currentValue && currentValue.length === 0}
      />
    </FormControl>
  );
};

export default EntitiesDropDownField;
